/**
 * React Query Advanced Retry Strategy
 * Implements exponential backoff with jitter and circuit breaker pattern
 */

import { logger } from "@/lib/logger";

/**
 * Interface para erros que podem ter propriedades adicionais
 */
interface ExtendedError extends Error {
  code?: string | number;
  status?: number;
  response?: {
    status?: number;
  };
}

type RetryValue =
  | boolean
  | number
  | ((failureCount: number, error: Error, context?: unknown) => boolean | Promise<boolean>);

type RetryDelayValue =
  | number
  | ((retryAttempt: number, error: Error, context?: unknown) => number | Promise<number>);

export interface RetryStrategyOptions {
  maxRetries?: number; // Maximum number of retry attempts (default: 3)
  baseDelay?: number; // Base delay in milliseconds (default: 1000)
  maxDelay?: number; // Maximum delay in milliseconds (default: 30000)
  factor?: number; // Exponential factor (default: 2)
  jitter?: boolean; // Add random jitter (default: true)
  circuitBreaker?: boolean; // Enable circuit breaker (default: true)
  circuitBreakerThreshold?: number; // Failure threshold for circuit breaker (default: 5)
  circuitBreakerTimeout?: number; // Circuit breaker timeout in ms (default: 60000)
  retryCondition?: (error: unknown) => boolean; // Custom retry condition
}

const DEFAULT_OPTIONS: Required<RetryStrategyOptions> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  factor: 2,
  jitter: true,
  circuitBreaker: true,
  circuitBreakerThreshold: 5,
  circuitBreakerTimeout: 60000,
  retryCondition: () => true,
};

/**
 * Circuit breaker state
 */
interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number | null;
  isOpen: boolean;
}

/**
 * Advanced retry strategy with exponential backoff and circuit breaker
 */
export class AdvancedRetryStrategy {
  private options: Required<RetryStrategyOptions>;
  private circuitBreakers: Map<string, CircuitBreakerState> = new Map();

  constructor(options: RetryStrategyOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Calculate retry delay with exponential backoff and jitter
   */
  calculateDelay(failureCount: number): number {
    const { baseDelay, factor, maxDelay, jitter } = this.options;

    // Exponential backoff
    let delay = baseDelay * Math.pow(factor, failureCount - 1);

    // Apply maximum delay cap
    delay = Math.min(delay, maxDelay);

    // Add jitter to prevent thundering herd
    if (jitter) {
      const jitterAmount = delay * 0.1; // 10% jitter
      delay = delay + (Math.random() - 0.5) * jitterAmount;
    }

    return Math.round(delay);
  }

  /**
   * Check if circuit breaker is open for a specific key
   */
  isCircuitBreakerOpen(key: string): boolean {
    if (!this.options.circuitBreaker) return false;

    const state = this.circuitBreakers.get(key);
    if (!state) return false;

    // Check if circuit breaker should be reset
    if (state.isOpen && state.lastFailureTime) {
      const timeSinceLastFailure = Date.now() - state.lastFailureTime;
      if (timeSinceLastFailure > this.options.circuitBreakerTimeout) {
        // Reset circuit breaker
        this.circuitBreakers.delete(key);
        logger.info("🔓 Circuit breaker reset", {
          eventType: "CIRCUIT_BREAKER_RESET",
          key,
          timeSinceLastFailureMs: timeSinceLastFailure,
        });
        return false;
      }
    }

    return state.isOpen;
  }

  /**
   * Record a failure for circuit breaker
   */
  recordFailure(key: string): void {
    if (!this.options.circuitBreaker) return;

    const state = this.circuitBreakers.get(key) || {
      failures: 0,
      lastFailureTime: null,
      isOpen: false,
    };

    state.failures++;
    state.lastFailureTime = Date.now();

    // Check if circuit breaker should open
    if (state.failures >= this.options.circuitBreakerThreshold) {
      state.isOpen = true;
      logger.warn("🔒 Circuit breaker opened", {
        eventType: "CIRCUIT_BREAKER_OPENED",
        key,
        failures: state.failures,
        threshold: this.options.circuitBreakerThreshold,
      });
    }

    this.circuitBreakers.set(key, state);
  }

  /**
   * Record a success for circuit breaker
   */
  recordSuccess(key: string): void {
    if (!this.options.circuitBreaker) return;

    // Remove circuit breaker on success
    if (this.circuitBreakers.has(key)) {
      const state = this.circuitBreakers.get(key)!;
      if (state.isOpen) {
        logger.info("🔓 Circuit breaker closed", {
          eventType: "CIRCUIT_BREAKER_CLOSED",
          key,
        });
      }
      this.circuitBreakers.delete(key);
    }
  }

  /**
   * Create retry function for React Query
   */
  createRetryFn(): RetryValue {
    return (failureCount, error, context) => {
      const key = this.getErrorKey(error);
      
      // Extrai informações da query do context se disponível
      const contextWithQueryKey = context as { queryKey?: unknown } | undefined;
      const queryKey = contextWithQueryKey?.queryKey 
        ? JSON.stringify(contextWithQueryKey.queryKey)
        : undefined;

      // Check circuit breaker
      if (this.isCircuitBreakerOpen(key)) {
        logger.warn("⚡ Circuit breaker open, skipping retry", {
          eventType: "CIRCUIT_BREAKER_SKIP_RETRY",
          errorKey: key,
          failureCount,
          queryKey,
        });
        return false;
      }

      // Check custom retry condition
      if (!this.options.retryCondition(error)) {
        logger.info("⏭️ Retry condition not met", {
          eventType: "RETRY_CONDITION_FAILED",
          errorKey: key,
          failureCount,
          queryKey,
        });
        return false;
      }

      // Check max retries
      if (failureCount >= this.options.maxRetries) {
        this.recordFailure(key);
        const extendedError = error as ExtendedError;
        logger.error("❌ Max retries reached", {
          eventType: "MAX_RETRIES_REACHED",
          errorKey: key,
          failureCount,
          maxRetries: this.options.maxRetries,
          errorMessage: error?.message,
          errorCode: extendedError?.code,
          errorStatus: extendedError?.status,
          errorName: error?.name,
          queryKey,
          // Inclui stack trace se disponível
          errorStack: error instanceof Error ? error.stack : undefined,
        });
        return false;
      }

      // Calculate delay
      const delay = this.calculateDelay(failureCount);

      logger.info("🔄 Retry attempt", {
        eventType: "RETRY_ATTEMPT",
        errorKey: key,
        failureCount,
        delayMs: delay,
        queryKey,
        errorMessage: error?.message,
      });

      return true;
    };
  }

  /**
   * Create retry delay function for React Query
   */
  createRetryDelayFn(): RetryDelayValue {
    return (failureCount, _error) => {
      return this.calculateDelay(failureCount);
    };
  }

  /**
   * Get unique key for error (for circuit breaker)
   */
  private getErrorKey(error: unknown): string {
    const extendedError = error as ExtendedError;
    if (extendedError?.code) return `error-code-${extendedError.code}`;
    if (extendedError?.status) return `error-status-${extendedError.status}`;
    if (extendedError?.message) {
      // Extract key information from error message
      const message = extendedError.message.toString();
      if (message.includes("ECONNREFUSED")) return "connection-refused";
      if (message.includes("ETIMEDOUT")) return "connection-timeout";
      if (message.includes("ENOTFOUND")) return "dns-not-found";
      if (message.includes("5xx")) return "server-error";
      if (message.includes("4xx")) return "client-error";
    }
    return "unknown-error";
  }
}

/**
 * Common retry conditions
 */
export const RetryConditions = {
  // Retry only on network errors
  networkOnly: (error: unknown) => {
    const extendedError = error as ExtendedError;
    const networkErrorCodes = ["ECONNREFUSED", "ETIMEDOUT", "ENOTFOUND"];
    return networkErrorCodes.some((code) =>
      extendedError?.code?.toString().includes(code) ||
      extendedError?.message?.toString().includes(code)
    );
  },

  // Retry only on server errors (5xx)
  serverErrorsOnly: (error: unknown) => {
    const extendedError = error as ExtendedError;
    const status = extendedError?.status || extendedError?.response?.status;
    return status !== undefined && status >= 500 && status < 600;
  },

  // Retry on all errors except client errors (4xx)
  nonClientErrors: (error: unknown) => {
    const extendedError = error as ExtendedError;
    const status = extendedError?.status || extendedError?.response?.status;
    if (status !== undefined && status >= 400 && status < 500) return false;
    return true;
  },

  // Never retry
  never: () => false,

  // Always retry
  always: () => true,
};

/**
 * Create default retry strategy
 */
export function createDefaultRetryStrategy(
  options?: RetryStrategyOptions
): {
  retry: RetryValue;
  retryDelay: RetryDelayValue;
} {
  const strategy = new AdvancedRetryStrategy(options);
  return {
    retry: strategy.createRetryFn(),
    retryDelay: strategy.createRetryDelayFn(),
  };
}

/**
 * Create network-only retry strategy
 */
export function createNetworkRetryStrategy(
  options?: Omit<RetryStrategyOptions, "retryCondition">
): {
  retry: RetryValue;
  retryDelay: RetryDelayValue;
} {
  const strategy = new AdvancedRetryStrategy({
    ...options,
    retryCondition: RetryConditions.networkOnly,
  });
  return {
    retry: strategy.createRetryFn(),
    retryDelay: strategy.createRetryDelayFn(),
  };
}

/**
 * Hook to use advanced retry strategy
 */
export function useAdvancedRetryStrategy(
  options?: RetryStrategyOptions
) {
  const strategyRef = useRef<AdvancedRetryStrategy | null>(null);

  if (!strategyRef.current) {
    strategyRef.current = new AdvancedRetryStrategy(options);
  }

  return useMemo(() => {
    const strategy = strategyRef.current!;
    return {
      retry: strategy.createRetryFn(),
      retryDelay: strategy.createRetryDelayFn(),
    };
  }, []);
}

// Import React hooks
import { useRef, useMemo } from "react";